import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Menu,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { notificationsAPI } from '../services/api';
import { AppNotification } from '../types/notification';
import { chatWebSocket, WSMessage } from '../services/chatWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

const PAGE_SIZE = 20;

type FilterMode = 'all' | 'unread';

const formatRelative = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr });
  } catch {
    return '';
  }
};

const NotificationsBell: React.FC = () => {
  const { user } = useAuth();
  const { showNotification, playNotificationSound } = useNotifications();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const open = Boolean(anchorEl);
  const showRef = useRef(showNotification);
  const playRef = useRef(playNotificationSound);
  const openRef = useRef(open);
  const filterRef = useRef(filter);

  useEffect(() => {
    showRef.current = showNotification;
  }, [showNotification]);
  useEffect(() => {
    playRef.current = playNotificationSound;
  }, [playNotificationSound]);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  const refreshCount = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnread(res.count);
    } catch {
      /* silent */
    }
  }, []);

  // Load page 1 for a given filter, replacing the current list.
  const loadFirstPage = useCallback(async (mode: FilterMode) => {
    setLoading(true);
    try {
      const res = await notificationsAPI.list({
        page: 1,
        size: PAGE_SIZE,
        ...(mode === 'unread' ? { is_read: false } : {}),
      });
      setItems(res.items);
      setPage(res.page);
      setPages(res.pages);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Append the next page to the existing list.
  const loadMore = useCallback(async () => {
    const next = page + 1;
    setLoadingMore(true);
    try {
      const res = await notificationsAPI.list({
        page: next,
        size: PAGE_SIZE,
        ...(filterRef.current === 'unread' ? { is_read: false } : {}),
      });
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...res.items.filter((n) => !seen.has(n.id))];
      });
      setPage(res.page);
      setPages(res.pages);
    } catch {
      /* silent */
    } finally {
      setLoadingMore(false);
    }
  }, [page]);

  // Initial load
  useEffect(() => {
    if (!user) return;
    refreshCount();
    loadFirstPage('all');
  }, [user, refreshCount, loadFirstPage]);

  // Re-sync on tab focus (covers WS-missed events). Refresh the list too when
  // the dropdown is open so its contents never disagree with the badge.
  useEffect(() => {
    const onFocus = () => {
      refreshCount();
      if (openRef.current) loadFirstPage(filterRef.current);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshCount, loadFirstPage]);

  // Subscribe to live notifications over the existing chat WebSocket
  useEffect(() => {
    if (!user) return;
    const unsubscribe = chatWebSocket.onMessage((msg: WSMessage) => {
      if (msg.type !== 'notification') return;
      const incoming = (msg as any).notification as AppNotification | undefined;
      if (!incoming) return;
      // Incoming notifications are always unread, so they belong in both views.
      setItems((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
      setUnread((c) => c + 1);
      try {
        playRef.current();
        showRef.current(incoming.title, {
          body: incoming.body || undefined,
          tag: `notif-${incoming.id}`,
        });
      } catch {
        /* silent */
      }
    });
    return unsubscribe;
  }, [user]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadFirstPage(filter);
    refreshCount();
  };
  const handleClose = () => setAnchorEl(null);

  const handleFilterChange = (_: React.SyntheticEvent, value: FilterMode) => {
    setFilter(value);
    loadFirstPage(value);
  };

  const handleClick = async (notif: AppNotification) => {
    setAnchorEl(null);
    if (!notif.is_read) {
      try {
        await notificationsAPI.markRead([notif.id]);
        setUnread((c) => Math.max(0, c - 1));
        setItems((prev) =>
          filter === 'unread'
            ? prev.filter((n) => n.id !== notif.id)
            : prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
        );
      } catch {
        /* silent */
      }
    }
    if (notif.link) navigate(notif.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markRead();
      setUnread(0);
      if (filter === 'unread') {
        setItems([]);
      } else {
        setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      }
    } catch {
      /* silent */
    }
  };

  const handleDismiss = async (
    e: React.MouseEvent<HTMLElement>,
    notif: AppNotification,
  ) => {
    e.stopPropagation();
    try {
      await notificationsAPI.dismiss(notif.id);
      setItems((prev) => prev.filter((n) => n.id !== notif.id));
      if (!notif.is_read) setUnread((c) => Math.max(0, c - 1));
    } catch {
      /* silent */
    }
  };

  const empty = useMemo(() => !loading && items.length === 0, [loading, items]);
  const hasMore = page < pages;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleOpen} size="large" sx={{ mr: 0.5 }}>
          <Badge badgeContent={unread} color="error" max={99}>
            {unread > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 520, p: 0 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
            </Typography>
            {unread > 0 && (
              <Badge
                badgeContent={unread}
                color="error"
                max={99}
                sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
              />
            )}
          </Box>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllRead}
            disabled={unread === 0}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Tout marquer lu
          </Button>
        </Box>
        <Tabs
          value={filter}
          onChange={handleFilterChange}
          variant="fullWidth"
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
        >
          <Tab value="all" label="Toutes" />
          <Tab
            value="unread"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                Non lues
                {unread > 0 && (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                      borderRadius: 10,
                      px: 0.75,
                      fontSize: 11,
                      lineHeight: '18px',
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {unread > 99 ? '99+' : unread}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>
        <Divider />
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {empty && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <NotificationsOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {filter === 'unread'
                ? 'Aucune notification non lue'
                : 'Aucune notification'}
            </Typography>
          </Box>
        )}
        {!loading &&
          items.map((n) => (
            <ListItemButton
              key={n.id}
              onClick={() => handleClick(n)}
              sx={{
                alignItems: 'flex-start',
                gap: 1,
                py: 1.25,
                bgcolor: n.is_read ? 'transparent' : 'action.hover',
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    fontWeight={n.is_read ? 400 : 700}
                    noWrap
                  >
                    {n.title}
                  </Typography>
                }
                secondary={
                  <Box>
                    {n.body && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {n.body}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {formatRelative(n.created_at)}
                    </Typography>
                  </Box>
                }
              />
              <IconButton
                size="small"
                onClick={(e) => handleDismiss(e, n)}
                aria-label="dismiss"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          ))}
        {!loading && hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Button
              size="small"
              onClick={loadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {loadingMore ? 'Chargement…' : 'Afficher plus'}
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationsBell;
